/**
 * Wann eine Datei den Papierkorb endgültig verlässt.
 *
 * Spiegelt `PropertyFileService.PurgeExpiredTrashAsync`: gelöscht **und** älter als die
 * Aufbewahrung **und** nicht gesperrt **und** ohne laufende Aufbewahrungsfrist. Der Text stand
 * vorher nirgends — der Nutzer las „Aufbew. bis" und musste selbst rechnen, ob seine Datei
 * morgen noch da ist.
 */
import type { TrashedFileResponse } from '@/shared/api';
import { formatDate } from '@/shared/lib/format';

/** Muss zu `PropertyTrashPurgeOptions.RetentionDays` (Standard 30) passen. */
export const TRASH_RETENTION_DAYS = 30;

export interface PurgeCountdown {
  label: string;
  /** `kept` = wird nie automatisch entfernt, `urgent` = eine Woche oder weniger. */
  tone: 'normal' | 'urgent' | 'kept';
}

export function purgeCountdown(
  item: Pick<TrashedFileResponse, 'deletedAt' | 'retentionUntil' | 'locked'>,
  now: Date = new Date(),
): PurgeCountdown {
  if (item.locked) {
    return { label: 'Bleibt dauerhaft erhalten — gesperrter Beleg', tone: 'kept' };
  }
  if (item.retentionUntil && new Date(item.retentionUntil) >= startOfDay(now)) {
    return {
      label: `Bleibt bis zum Ende der Aufbewahrung am ${formatDate(item.retentionUntil)}`,
      tone: 'kept',
    };
  }
  if (!item.deletedAt) {
    return { label: 'Löschdatum nicht hinterlegt', tone: 'normal' };
  }
  const days = TRASH_RETENTION_DAYS - daysSince(item.deletedAt, now);
  if (days <= 0) {
    return { label: 'Wird beim nächsten Aufräumlauf entfernt', tone: 'urgent' };
  }
  const label = days === 1 ? 'Wird morgen entfernt' : `Wird in ${days} Tagen entfernt`;
  return { label, tone: days <= 7 ? 'urgent' : 'normal' };
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function daysSince(iso: string, now: Date): number {
  const ms = now.getTime() - new Date(iso).getTime();
  return Math.floor(ms / 86_400_000);
}
